import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { from, Observable } from 'rxjs';
import { GamesService } from '../../services/games/games.service';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { GamesLauncherService } from '../../services/games/games-launcher.service';
import { isDesktop } from '../../helpers/device';
import { UserService } from '../../services/user/user.service';
import { UserProfileService } from '../../services/user/user-profile.service';
import { ModalService } from '../../modal-v2/modal.service';

@Injectable({
  providedIn: 'root'
})
export class GamePageGuard implements CanActivate {
  private _router = inject(Router);
  private _games = inject(GamesService);
  private _launcher = inject(GamesLauncherService);
  private _user = inject(UserService);
  private _userProfile = inject(UserProfileService);
  private _modal = inject(ModalService);


  /**
   * Check if game exists in current navigation state
   * and get game from backend if not exists
   *
   * @param next
   * @param state
   */
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    const game = this._router.getCurrentNavigation()?.extras?.state?.game;
    const slug = this._resolveGameSlug(next);

    return this._userProfile.resolveFields('deposit').pipe(
      first(),
      switchMap(fields => {
        if (fields.length && this._user.auth) {
          return from(
            import(
              '../../modal-v2/components/regular/auth-fields-missed-update/auth-fields-missed-update.component'
              ).then(async component => {
              await this._modal.openLazy(
                component?.AuthFieldsMissedUpdateComponent,
                {
                  template: 'CLEAR',
                  data: {
                    context: 'deposit'
                  }
                },
              ).then(res => {
                res.onResult().pipe(first()).subscribe(updated => {
                  if (updated) this._router.navigateByUrl(`/play/${slug}`);
                });
              });
              return false;
            }),
          );
        }

        if (game) {
          this._games.currentGameState = game;
          return this._user.auth$.pipe(
            first(),
            map(() => this._launcher.checkGameAvailability(game)),
          );
        }

        return this._user.auth$.pipe(
          first(),
          switchMap(() => this._games.list({ slug })),
          filter(response => !!response),
          first(),
          map(response => {
            if (response.gameList[0]) {
              this._games.currentGameState = response.gameList[0];
            }
            return this._launcher.checkGameAvailability(
              response.gameList[0] || null,
            );
          }),
        );
      }),
    );
  }

  private _resolveGameSlug(route: ActivatedRouteSnapshot): string {
    if (route.queryParams['mobile-alias'] && !isDesktop()) {
      return route.queryParams['mobile-alias'];
    }
    return route.params.slug;
  }


}
